import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react'
import { useSelector } from 'react-redux'
import { WIZARD_SCREEN_TYPES } from '../../constants'
import {
	GROOMING_APPOINTMENT_WORKFLOW,
	GROOMING_APPOINTMENT_SECTIONS,
	DEFAULT_GROOMING_APPOINTMENT_DETAILS
} from './config'
import WizardDialog from '../../components/WizardDialog'
import useGroomingVisit from '../../../../hooks/useGroomingVisit'
import useWizardRouterHistory from '../../hooks/useWizardRouterHistory'
import SectionNavigator from '../../components/SectionNavigator'
import ExitWizardDialog from '../../components/ExitWizardDialog'
import GroomingAppointmentWizardContext from './GroomingAppointmentWizardContext'
import { PLACEHOLDER_DATE } from '../../../../utils'
import DifferentFacilityBookingWarning from '../../../Common/DifferentFacilityBookingWarning'

const GroomingAppointmentWizard = ({ step = 0, open = false, onClose, updatedVisit, isRebook = false }) => {
	const [currentStep, setCurrentStep] = useState(step)
	const [canProceedFunc, setCanProceedFunc] = useState(() => () => true)
	const [showExitWizardDialog, setShowExitWizardDialog] = useState(false)
	const wizardInitialized = useRef(false)

	const { loading, update, updateAddOns, uploadReferencePhoto, updateDateAndTime, facility } = useGroomingVisit()
	const household = useSelector((state) => state.specific.households.selected)
	const { setStepInHistory, clearHistory } = useWizardRouterHistory()
	const user = useSelector((state) => state.general.user.user)

	// Context Related State -----------------------------------------------------
	const setWizardAttributeAndValues = (newValues) => {
		setWizardContextProvider({
			grooming_appointment: { ...newValues },
			setWizardAttributeAndValues
		})
	}

	const [wizardContextProvider, setWizardContextProvider] = useState({
		grooming_appointment: { ...DEFAULT_GROOMING_APPOINTMENT_DETAILS },
		setWizardAttributeAndValues
	})
	// ---------------------------------------------------------------------------

	const REQUIRED_WORKFLOW = useMemo(() => {
		const { grooming_appointment } = wizardContextProvider
		const filteredWorkflow = GROOMING_APPOINTMENT_WORKFLOW.filter((step) => {
			const { display_step_condition } = step
			if (!display_step_condition || display_step_condition(grooming_appointment)) {
				return step
			}
		})
		return filteredWorkflow
	}, [wizardContextProvider])

	const requiredSteps = useMemo(() => REQUIRED_WORKFLOW.length, [REQUIRED_WORKFLOW])

	useEffect(() => {
		const { grooming_appointment, setWizardAttributeAndValues } = wizardContextProvider
		if (updatedVisit) {
			const updatedVisitValues = { ...grooming_appointment }

			for (const key in updatedVisit) {
				if (updatedVisitValues[key] !== undefined) {
					updatedVisitValues[key] = updatedVisit[key]
				}
				// have to manually set it to none if '' to make select component work >:/
				if (key === 'preferred_staff_id') {
					updatedVisitValues['preferred_staff_id'] =
						updatedVisit[key] === '' ? 'none' : updatedVisit[key]
				}

				if (key === 'pets') {
					const pets = updatedVisit[key]
					if (pets && pets.length > 0) {
						const pet = pets[0]
						updatedVisitValues['petName'] = pet.name || 'Doggo'
						updatedVisitValues['petId'] = pet._id || ''
					}
				}
			}
			if (isRebook) {
				updatedVisitValues['isRebook'] = isRebook
				const [curDate] = updatedVisit['dates']
				if (curDate === PLACEHOLDER_DATE) {
					updatedVisitValues['dates'] = []
				}
				updatedVisitValues['applied_promo'] = null
			}
			setWizardAttributeAndValues(updatedVisitValues)
		}
	}, [updatedVisit, isRebook])

	useEffect(() => {
		const stepInt = parseInt(step, 10)
		setCurrentStep(stepInt)
	}, [step])

	useEffect(() => {
		if (isRebook && !wizardInitialized.current) {
			const foundStep = REQUIRED_WORKFLOW.findIndex((step) => step.visit_attr === 'preferred_staff_id')
			if (foundStep > -1) {
				setCurrentStep(foundStep)
				wizardInitialized.current = true
			}
		}
	}, [isRebook, REQUIRED_WORKFLOW, setCurrentStep, wizardInitialized])

	const userFirstName = useMemo(() => {
		if (!user || !user.displayName) {
			return 'WhoAreYou?'
		}

		const [first] = user.displayName.split(' ')
		return first || 'Skipper'
	}, [user])

	const handleCloseRequest = useCallback(() => {
		setShowExitWizardDialog(true)
	}, [setShowExitWizardDialog])

	const handleCloseRequestCancel = useCallback(() => {
		setShowExitWizardDialog(false)
	}, [setShowExitWizardDialog])

	const handleStart = useCallback(() => {
		setCurrentStep(1)
		setStepInHistory(1)
	}, [setCurrentStep, setStepInHistory])

	const handleExit = useCallback(() => {
		if (onClose) {
			clearHistory()
			onClose()
		}
	}, [onClose, clearHistory])

	const handleOnClose = useCallback(() => {
		if (onClose && !loading) {
			setShowExitWizardDialog(false)
			clearHistory()
			onClose()
			// add the following back in if we want to clear the dropoff time if user bails from wizard
			// update({ dropoff_time: '', preferred_staff_id: '' })
		}
	}, [onClose, loading, setShowExitWizardDialog, clearHistory, update])

	const canProceed = useMemo(() => {
		return canProceedFunc(wizardContextProvider.grooming_appointment)
	}, [canProceedFunc, wizardContextProvider])

	/**
	 * returns the configuration object for the current step
	 */
	const currentWorflowStepConfig = useMemo(() => {
		return (
			REQUIRED_WORKFLOW[currentStep] || {
				section: null,
				type: null,
				component: null
			}
		)
	}, [currentStep, REQUIRED_WORKFLOW])

	const hideActionsOtherThanClose = useMemo(() => {
		const { type } = currentWorflowStepConfig
		if (type === WIZARD_SCREEN_TYPES.Start || type === WIZARD_SCREEN_TYPES.Exit) {
			return true
		}
		return false
	}, [currentWorflowStepConfig])

	const hideNext = useMemo(() => {
		const { type } = currentWorflowStepConfig
		if (type === WIZARD_SCREEN_TYPES.Invoice) {
			return true
		}
		return false
	}, [currentWorflowStepConfig])

	const currentSection = useMemo(() => {
		const { section, type } = currentWorflowStepConfig
		if (type && type === WIZARD_SCREEN_TYPES.Intro) {
			return null
		}

		return section
	}, [currentWorflowStepConfig])

	const currentAttribute = useMemo(() => {
		const { visit_attr } = currentWorflowStepConfig || ''
		return visit_attr
	}, [currentWorflowStepConfig])

	const requiresApiUpdate = useMemo(() => {
		const { requires_api_update } = currentWorflowStepConfig || false
		return requires_api_update
	}, [currentWorflowStepConfig])

	const handleUpdate = useCallback(async () => {
		const { grooming_appointment } = wizardContextProvider
		const { _id: visitId, petId } = grooming_appointment
		const value = grooming_appointment[currentAttribute]
		if (value !== undefined && requiresApiUpdate && visitId > '') {
			switch (currentAttribute) {
				case 'add_on_services':
					await updateAddOns({ visitId, petId, addons: value })
					break
				case 'reference_photo_file':
					if (value !== null) {
						const data = await uploadReferencePhoto({ visitId, file: value })
						const {
							data: { reference_photo_url }
						} = data
						const newValues = { ...grooming_appointment }
						newValues[currentAttribute] = null
						newValues['reference_photo_url'] = reference_photo_url
						setWizardAttributeAndValues(newValues)
					}
					break
				case 'dates':
					const { dates, dropoff_time } = grooming_appointment
					await updateDateAndTime({ visitId, updates: { dates, dropoff_time } })
				case 'preferred_staff_id':
					if (value === 'none') {
						await update({ visitId, updates: { [currentAttribute]: '' } })
					} else {
						await update({ visitId, updates: { [currentAttribute]: value } })
					}
					break
				default:
					await update({ visitId, updates: { [currentAttribute]: value } })
					break
			}
		}
	}, [wizardContextProvider, currentAttribute, requiresApiUpdate, update, updateAddOns, uploadReferencePhoto])

	const incrementStep = useCallback(
		async (params = { by: 1, withSave: true }) => {
			const curStep = parseInt(currentStep, 10)
			if (curStep !== requiredSteps) {
				if (params.withSave) {
					await handleUpdate()
				}
				const goToStep = curStep + params.by
				setCurrentStep(goToStep)
				setStepInHistory(goToStep)
			}
		},
		[currentStep, setCurrentStep, setStepInHistory, requiredSteps, handleUpdate]
	)

	const decrementStep = useCallback(
		async (params = { by: 1, withSave: true }) => {
			const curStep = parseInt(currentStep, 10)
			if (curStep !== 0) {
				if (params.withSave) {
					await handleUpdate()
				}
				const goToStep = curStep - params.by
				setCurrentStep(goToStep)
				setStepInHistory(goToStep)
			}
		},
		[currentStep, setCurrentStep, setStepInHistory, handleUpdate]
	)

	const currentComponent = useMemo(() => {
		const { component, can_proceed_condition, visit_attr, type } = currentWorflowStepConfig
		const { grooming_appointment } = wizardContextProvider

		if (!component) {
			return <div>I dont seem to have that step!</div>
		}

		if (can_proceed_condition) {
			setCanProceedFunc(() => can_proceed_condition)
		} else {
			setCanProceedFunc(() => () => true)
		}

		const props = {}
		if (type === WIZARD_SCREEN_TYPES.Start) {
			props['onAction'] = handleStart
		}
		if (type === WIZARD_SCREEN_TYPES.Exit) {
			props['onAction'] = handleExit
		}
		if (visit_attr) {
			props['visit_attr'] = visit_attr
		}
		if (type === WIZARD_SCREEN_TYPES.Invoice) {
			// The invoice view is the last screen and the onComplete
			// is called after the booking is confirmed.
			props['onComplete'] = incrementStep
		}

		props['petName'] = grooming_appointment.petName
		props['userName'] = userFirstName

		return component(props)
	}, [
		currentStep,
		currentWorflowStepConfig,
		wizardContextProvider,
		userFirstName,
		handleStart,
		handleExit,
		incrementStep,
		decrementStep
	])

	return (
		<GroomingAppointmentWizardContext.Provider value={{ wizardContextProvider }}>
			<div>
				<WizardDialog
					open={open}
					hideBack={hideActionsOtherThanClose}
					hideFooter={hideActionsOtherThanClose || hideNext}
					disableNext={!canProceed || loading}
					disableBack={loading}
					onClose={handleCloseRequest}
					onNext={incrementStep}
					onBack={decrementStep}
					loading={loading}>
					<div
						style={{
							display: 'flex',
							flexDirection: 'column',
							flex: 1,
							gap: '1rem',
							opacity: loading ? 0.5 : 1
						}}>
						<DifferentFacilityBookingWarning
							facility={facility}
							defaultFacilityId={household?.default_facility_id}
						/>
						{currentSection ? (
							<SectionNavigator
								sections={GROOMING_APPOINTMENT_SECTIONS}
								activeSectionId={currentSection}
							/>
						) : null}
						{currentComponent}
					</div>
				</WizardDialog>
				<ExitWizardDialog
					title="Exit Grooming Appointment Wizard?"
					description="Are you sure you want to exit the appointment setup wizard? You will need to use the regular appointment form to complete the appointment."
					open={showExitWizardDialog}
					onExit={handleOnClose}
					onCancel={handleCloseRequestCancel}
				/>
			</div>
		</GroomingAppointmentWizardContext.Provider>
	)
}

export default GroomingAppointmentWizard
